"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import SouthIcon from "@mui/icons-material/South";
import type { SponsorCampaign } from "../campaignTypes";
import { PORTAL_MAX_WIDTH, portalFont, portalShellSx } from "../partnerTheme";
import {
  foodTourConfig,
  type FoodTourConfig,
  type FoodTourVenue,
} from "../campaigns/food-tour";
import { hexcladFoodTourConfig } from "../campaigns/hexclad-food-tour";

const LINE = "rgba(255,255,255,0.12)";
const MUTED = "rgba(244,245,247,0.62)";
const CARD_BG = "rgba(255,255,255,0.04)";
const RADIUS = "16px";

const FoodTourCtx = createContext<FoodTourConfig>(foodTourConfig);

function useFoodTour() {
  return useContext(FoodTourCtx);
}

function configForCampaign(campaign: SponsorCampaign): FoodTourConfig {
  if (campaign.campaignSlug === "hexclad-food-tour") return hexcladFoodTourConfig;
  return foodTourConfig;
}

/**
 * Custom Food Tour ABM microsite. Visual language mirrors Amex/BILT residency portals.
 */
export default function FoodTourPage({ campaign }: { campaign: SponsorCampaign }) {
  const cfg = useMemo(() => configForCampaign(campaign), [campaign.campaignSlug]);
  return (
    <FoodTourCtx.Provider value={cfg}>
      <Box sx={{ ...portalShellSx(campaign), bgcolor: "#000" }}>
        <Header />
        <Hero />
        <BigIdeaSection />
        <TwoSidesSection />
        <TourSection />
        <TypicalNightSection />
        <WhyBrandSection />
        <HowBrandSection />
        {cfg.sponsorshipTiers ? <SponsorshipTiersSection /> : null}
        <ContentEngineSection />
        <ReachSection />
        <VisionSection />
        <Footer contactEmail={campaign.closing.contact.email} />
      </Box>
    </FoodTourCtx.Provider>
  );
}

function Header() {
  const cfg = useFoodTour();
  const brand = cfg.brandName.trim();
  const whyLabel = brand ? `Why ${brand}` : "Why partner";
  const howLabel = brand ? `How ${brand} shows up` : "How you show up";
  const nav = [
    { id: "big-idea", label: "The big idea" },
    { id: "two-sides", label: "Two sides" },
    { id: "the-tour", label: "The tour" },
    { id: "typical-night", label: "A night" },
    { id: "why-brand", label: whyLabel },
    { id: "how-brand", label: howLabel },
    ...(cfg.sponsorshipTiers ? [{ id: "tiers", label: "Sponsorship" }] : []),
    { id: "content", label: "Content" },
    { id: "reach", label: "Reach" },
    { id: "vision", label: "Next" },
  ];
  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        bgcolor: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          height: { xs: 64, md: 72 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.25, md: 1.75 }, minWidth: 0 }}>
          {brand || cfg.brandLogoUrl ? (
            <>
              <BrandMark />
              <Box
                aria-hidden
                sx={{
                  width: "1px",
                  height: { xs: 20, md: 28 },
                  bgcolor: "rgba(255,255,255,0.35)",
                  flexShrink: 0,
                }}
              />
            </>
          ) : null}
          <Box
            component="img"
            src="https://storage.googleapis.com/cuo-website/comedyuo-logo-white.png"
            alt="ComedyUO"
            sx={{
              height: { xs: 36, md: 48 },
              width: "auto",
              display: "block",
              flexShrink: 0,
              objectFit: "contain",
            }}
          />
        </Box>

        <Box
          component="nav"
          sx={{
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            gap: 2,
            overflowX: "auto",
            minWidth: 0,
          }}
        >
          {nav.map((item) => (
            <Link
              key={item.id}
              href={`#${item.id}`}
              underline="none"
              sx={{
                fontFamily: portalFont,
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: MUTED,
                whiteSpace: "nowrap",
                "&:hover": { color: "#fff" },
              }}
            >
              {item.label}
            </Link>
          ))}
        </Box>

        <Button
          href="#vision"
          sx={{
            fontFamily: portalFont,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontSize: "0.6875rem",
            color: "#0a0a0a",
            bgcolor: "#fff",
            borderRadius: "999px",
            px: 2,
            py: 0.85,
            flexShrink: 0,
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
        >
          Talk with us
        </Button>
      </Box>
    </Box>
  );
}

function BrandMark() {
  const cfg = useFoodTour();
  if (cfg.brandLogoUrl) {
    return (
      <Box
        component="img"
        src={cfg.brandLogoUrl}
        alt={cfg.brandName}
        sx={{
          height: { xs: 26, md: 32 },
          width: "auto",
          maxWidth: 120,
          display: "block",
          objectFit: "contain",
          filter: "brightness(0) invert(1)",
        }}
      />
    );
  }
  return (
    <Typography
      sx={{
        fontFamily: portalFont,
        fontWeight: 700,
        fontSize: { xs: "0.95rem", md: "1.05rem" },
        letterSpacing: "-0.02em",
        color: "#fff",
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.brandName}
    </Typography>
  );
}

function Hero() {
  const cfg = useFoodTour();
  const accent = cfg.accentColor;
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        minHeight: { xs: "88vh", md: "92vh" },
        display: "flex",
        alignItems: "stretch",
        isolation: "isolate",
        bgcolor: "#000",
      }}
    >
      <MediaFill
        src={cfg.images.hero}
        alt="Comedy night during the ComedyUO Food Tour"
        objectPosition="center 22%"
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background: {
            xs: "radial-gradient(ellipse 90% 70% at 0% 0%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 55%, transparent 75%), linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.55) 42%, rgba(0,0,0,0.92) 100%)",
            md: "radial-gradient(ellipse 70% 65% at 8% 12%, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.45) 42%, transparent 70%), linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.7) 36%, rgba(0,0,0,0.22) 70%, rgba(0,0,0,0.45) 100%), linear-gradient(180deg, rgba(0,0,0,0.28) 0%, transparent 40%, rgba(0,0,0,0.82) 100%)",
          },
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: PORTAL_MAX_WIDTH,
          width: "100%",
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          pt: { xs: 7, md: 12 },
          pb: { xs: 4, md: 5 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: { xs: "88vh", md: "92vh" },
        }}
      >
        <FadeUp>
          <Typography sx={{ ...eyebrowSx, color: accent, mb: 1.5 }}>{cfg.timingLabel}</Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: portalFont,
              fontWeight: 700,
              color: "#fff",
              fontSize: "clamp(2.2rem, 5.5vw + 0.4rem, 4.25rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.035em",
              mb: { xs: 1.5, md: 2 },
              maxWidth: 680,
            }}
          >
            {cfg.tourTitle}.
          </Typography>
          <Typography
            sx={{
              fontFamily: portalFont,
              color: "rgba(244,245,247,0.82)",
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.4,
              maxWidth: 480,
              mb: 2.5,
            }}
          >
            {cfg.positioning}
          </Typography>
          <Button
            href="#big-idea"
            endIcon={<SouthIcon sx={{ fontSize: 16 }} />}
            sx={{
              alignSelf: "flex-start",
              fontFamily: portalFont,
              fontWeight: 600,
              textTransform: "none",
              color: "#0a0a0a",
              bgcolor: "#fff",
              borderRadius: "999px",
              px: 2.25,
              py: 1.1,
              "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
            }}
          >
            Enter the tour
          </Button>
        </FadeUp>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
            gap: { xs: 1.25, md: 2 },
            pt: 3,
            borderTop: `1px solid ${LINE}`,
          }}
        >
          {cfg.heroFacts.map((fact) => (
            <Box key={fact.label}>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: { xs: "1.4rem", md: "1.7rem" },
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                  mb: 0.4,
                }}
              >
                {fact.value}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                {fact.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function BigIdeaSection() {
  const cfg = useFoodTour();
  const accent = cfg.accentColor;
  return (
    <Box
      id="big-idea"
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        pt: { xs: 5, md: 7 },
        pb: { xs: 2, md: 2.5 },
        scrollMarginTop: 88,
      }}
    >
      <FadeUp>
        <Typography sx={{ ...eyebrowSx, color: accent, mb: 1 }}>The big idea</Typography>
        <Typography
          component="h2"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: "clamp(1.7rem, 2.1vw + 1rem, 2.55rem)",
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
            mb: 1.5,
            maxWidth: 640,
          }}
        >
          Two sides of New York. One tour.
        </Typography>
        <Typography sx={{ ...bodySx, mb: 1.25, maxWidth: 640 }}>
          Guests get both New Yorks: the local neighborhood classics, then the Times Square icons. We feature the food and the full experience. The comedy shows are the vehicle that brings everyone into the room together.
        </Typography>
        <Typography sx={{ ...bodySx, maxWidth: 640 }}>
          Each night turns a working restaurant into an intimate, sold-out comedy venue. Iconic bites stay on the menu. Owners and operators stay in the story. The lineup stays a surprise.
        </Typography>
      </FadeUp>
    </Box>
  );
}

function TwoSidesSection() {
  const cfg = useFoodTour();
  return (
    <Box
      id="two-sides"
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        pt: { xs: 2.5, md: 3 },
        pb: { xs: 5, md: 7 },
        scrollMarginTop: 88,
      }}
    >
      <FadeUp>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          <SplitCard
            label="Week 1"
            title={cfg.weekCopy.neighborhood.title}
            body={cfg.weekCopy.neighborhood.body}
            image={cfg.images.neighborhood}
            imageAlt="Neighborhood Food Tour venue"
          />
          <SplitCard
            label="Week 2"
            title={cfg.weekCopy.timesSquare.title}
            body={cfg.weekCopy.timesSquare.body}
            image={cfg.images.timesSquare}
            imageAlt="Times Square Food Tour energy"
          />
        </Box>
      </FadeUp>
    </Box>
  );
}

function SplitCard({
  label,
  title,
  body,
  image,
  imageAlt,
}: {
  label: string;
  title: string;
  body: string;
  image: string;
  imageAlt: string;
}) {
  const accent = useFoodTour().accentColor;
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: RADIUS,
        overflow: "hidden",
        border: `1px solid ${LINE}`,
        minHeight: { xs: 320, md: 400 },
        bgcolor: "#111",
      }}
    >
      <MediaFill src={image} alt={imageAlt} />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.78) 68%, rgba(0,0,0,0.92) 100%)",
        }}
      />
      <Box sx={{ position: "absolute", inset: 0, p: { xs: 2.25, md: 3 }, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        <Typography sx={{ ...eyebrowSx, color: accent, mb: 0.75 }}>{label}</Typography>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "1.45rem", md: "1.75rem" },
            letterSpacing: "-0.03em",
            mb: 0.75,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontFamily: portalFont, color: "rgba(244,245,247,0.78)", fontSize: "0.95rem", lineHeight: 1.45, maxWidth: 420 }}>
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

function TourSection() {
  const cfg = useFoodTour();
  const neighborhood = cfg.venues.filter((v) => v.week === "neighborhood");
  const timesSquare = cfg.venues.filter((v) => v.week === "timesSquare");
  return (
    <Section id="the-tour" eyebrow="The tour" title={cfg.tourSectionTitle}>
      <Typography sx={{ ...bodySx, mb: { xs: 2, md: 2.5 }, maxWidth: 620 }}>
        {cfg.tourSectionIntro}
      </Typography>
      <WeekBlock label="Neighborhood Classics" venues={neighborhood} />
      <Box sx={{ height: { xs: 2.5, md: 3.5 } }} />
      <WeekBlock label="Times Square Favorites" venues={timesSquare} />
    </Section>
  );
}

function TypicalNightSection() {
  const cfg = useFoodTour();
  const accent = cfg.accentColor;
  const { nightFlow } = cfg;
  return (
    <Section id="typical-night" eyebrow="Run of show" title={nightFlow.title}>
      <Typography sx={{ ...bodySx, mb: { xs: 2.5, md: 3 }, maxWidth: 560 }}>
        {nightFlow.intro}
      </Typography>
      <Box sx={{ borderTop: `1px solid ${LINE}` }}>
        {nightFlow.steps.map((step) => (
          <Box
            key={step.title}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "64px 1fr", md: "88px 1fr" },
              gap: { xs: 1.5, md: 2.5 },
              py: { xs: 2, md: 2.25 },
              borderBottom: `1px solid ${LINE}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: accent,
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                letterSpacing: "-0.02em",
                pt: 0.15,
              }}
            >
              {step.time}
            </Typography>
            <Box>
              <Typography
                sx={{
                  fontFamily: portalFont,
                  fontWeight: 700,
                  color: "#fff",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  letterSpacing: "-0.02em",
                  mb: 0.4,
                }}
              >
                {step.title}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.92rem", lineHeight: 1.5, maxWidth: 520 }}>
                {step.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function WeekBlock({ label, venues }: { label: string; venues: FoodTourVenue[] }) {
  const accent = useFoodTour().accentColor;
  return (
    <Box>
      <Typography sx={{ ...eyebrowSx, color: accent, mb: 2 }}>{label}</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "repeat(auto-fit, minmax(200px, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {venues.map((venue) => (
          <VenueCard key={venue.id} venue={venue} />
        ))}
      </Box>
    </Box>
  );
}

function VenueCard({ venue }: { venue: FoodTourVenue }) {
  const accent = useFoodTour().accentColor;
  return (
    <Box
      sx={{
        borderRadius: RADIUS,
        overflow: "hidden",
        border: `1px solid ${LINE}`,
        bgcolor: CARD_BG,
        display: "flex",
        flexDirection: "column",
        minHeight: 280,
      }}
    >
      <Box sx={{ position: "relative", height: 140, bgcolor: "#151515" }}>
        <MediaFill src={venue.imageUrl} alt={venue.name} />
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            px: 1,
            py: 0.4,
            borderRadius: "999px",
            bgcolor: "rgba(0,0,0,0.7)",
            border: `1px solid ${LINE}`,
            fontFamily: portalFont,
            fontSize: "0.65rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#fff",
          }}
        >
          {venue.dateLabel}
        </Box>
      </Box>
      <Box sx={{ p: 1.75, display: "flex", flexDirection: "column", gap: 0.6, flex: 1 }}>
        <Typography sx={{ fontFamily: portalFont, fontWeight: 700, color: "#fff", letterSpacing: "-0.02em" }}>
          {venue.name}
        </Typography>
        <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.8rem" }}>{venue.cuisine}</Typography>
        <Typography sx={{ fontFamily: portalFont, color: "rgba(244,245,247,0.72)", fontSize: "0.82rem", lineHeight: 1.45, mt: "auto", pt: 1 }}>
          {venue.note}
        </Typography>
        <Typography sx={{ fontFamily: portalFont, color: accent, fontSize: "0.75rem", mt: 1 }}>
          ~{venue.capacity} capacity
        </Typography>
      </Box>
    </Box>
  );
}

function WhyBrandSection() {
  const cfg = useFoodTour();
  const accent = cfg.accentColor;
  const whyLabel = cfg.brandName.trim() ? `Why ${cfg.brandName.trim()}` : "Why partner";
  return (
    <Box
      id="why-brand"
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        py: { xs: 5.5, md: 8 },
        scrollMarginTop: 88,
      }}
    >
      <FadeUp>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
            gap: { xs: 2.5, md: 4 },
            alignItems: "center",
          }}
        >
          <Box>
            <Typography sx={{ ...eyebrowSx, color: accent, mb: 1 }}>{whyLabel}</Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: "clamp(1.55rem, 1.9vw + 0.9rem, 2.2rem)",
                lineHeight: 1.15,
                letterSpacing: "-0.03em",
                mb: 2,
              }}
            >
              {cfg.partnershipThesis}
            </Typography>
            <Box sx={{ display: "grid", gap: 1.5 }}>
              {cfg.brandWhyBody.map((paragraph) => (
                <Typography key={paragraph.slice(0, 24)} sx={bodySx}>
                  {paragraph}
                </Typography>
              ))}
            </Box>
          </Box>
          <MediaFrame
            src={cfg.images.content.ownerInterview}
            alt="Gary Greengrass interview during the Food Tour"
            ratio="4 / 3"
            objectPosition="center 42%"
          />
        </Box>
      </FadeUp>
    </Box>
  );
}

function HowBrandSection() {
  const cfg = useFoodTour();
  const accent = cfg.accentColor;
  const howLabel = cfg.brandName.trim()
    ? `How ${cfg.brandName.trim()} shows up`
    : "How a partner shows up";
  const blocks = [
    {
      id: "on-site",
      title: "On-site experience",
      eyebrow: cfg.brandActivations.onSiteEyebrow || "Entryways, rooms, and live shout-outs",
      items: cfg.brandActivations.onSite,
    },
    {
      id: "content",
      title: "Original content",
      eyebrow: cfg.brandActivations.contentEyebrow || "Promo, stories, and the waitlist blast",
      items: cfg.brandActivations.content,
    },
    ...(cfg.brandActivations.guestAccess.length
      ? [
          {
            id: "guest",
            title: "Guest access and product moments",
            eyebrow: cfg.brandActivations.guestEyebrow || "Seats, exclusivity, and giveaways",
            items: cfg.brandActivations.guestAccess,
          },
        ]
      : []),
  ];
  const columns = blocks.length === 2 ? "1fr 1fr" : "1fr 1fr 1fr";
  return (
    <Section
      id="how-brand"
      eyebrow={howLabel}
      title={cfg.howBrandTitle || "Built into the night, not stuck on top of it."}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: columns },
          gap: 2,
        }}
      >
        {blocks.map((block) => (
          <Box
            key={block.id}
            sx={{
              p: { xs: 2.25, md: 2.75 },
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              minHeight: { md: 360 },
            }}
          >
            <Typography sx={{ ...eyebrowSx, color: accent }}>{block.eyebrow}</Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: "1.25rem",
                letterSpacing: "-0.02em",
                mb: 2,
              }}
            >
              {block.title}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25, display: "grid", gap: 1.25 }}>
              {block.items.map((item) => (
                <Typography
                  key={item}
                  component="li"
                  sx={{ fontFamily: portalFont, color: "rgba(244,245,247,0.78)", fontSize: "0.92rem", lineHeight: 1.5 }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function SponsorshipTiersSection() {
  const cfg = useFoodTour();
  const tiers = cfg.sponsorshipTiers;
  const accent = cfg.accentColor;
  if (!tiers) return null;
  return (
    <Section id="tiers" eyebrow="Sponsorship tiers" title="Four ways to show up.">
      <Typography sx={{ ...bodySx, mb: { xs: 2.5, md: 3 }, maxWidth: 720 }}>
        Option A: partner on the <Box component="span" sx={{ color: "#fff", fontWeight: 600 }}>full tour</Box> as a
        Supporter or Sponsor. Option B: partner on <Box component="span" sx={{ color: "#fff", fontWeight: 600 }}>just the 1st week</Box>{" "}
        (the five Neighborhood Classics shows), with the same tier deliverables cut down to those nights only. Week 2 is not included.
      </Typography>

      <Typography sx={{ ...eyebrowSx, color: accent, mb: 1.5 }}>Full tour · all 7 nights</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.15fr 1.35fr" },
          gap: 1.5,
          alignItems: "start",
          mb: { xs: 3.5, md: 4.5 },
        }}
      >
        <Box
          sx={{
            p: { xs: 2.25, md: 2.75 },
            borderRadius: RADIUS,
            border: `1px solid ${LINE}`,
            bgcolor: CARD_BG,
          }}
        >
          <Typography sx={{ ...eyebrowSx, color: accent, mb: 2 }}>Tour dates</Typography>
          <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
            Week 1 · Neighborhood
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2.5 }}>
            {tiers.weekOneDates.map((d) => (
              <DateChip key={d} label={d} accent={accent} />
            ))}
          </Box>
          <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
            Week 2 · Times Square
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {tiers.weekTwoDates.map((d) => (
              <DateChip key={d} label={d} accent={accent} />
            ))}
          </Box>
        </Box>

        <TierCard
          name="Supporter · full tour"
          price={tiers.fullTour.supporterPrice}
          accent={accent}
          youGet={tiers.fullTour.supporterGets}
          youProvide={tiers.fullTour.youProvide}
        />
        <TierCard
          name="Sponsor · full tour"
          price={tiers.fullTour.sponsorPrice}
          accent={accent}
          highlight
          youGet={tiers.fullTour.sponsorGets}
          youProvide={tiers.fullTour.youProvide}
        />
      </Box>

      <Typography sx={{ ...eyebrowSx, color: accent, mb: 1.5 }}>Or just the 1st week · 5 Neighborhood Classics shows</Typography>
      <Box
        sx={{
          p: { xs: 2.25, md: 2.75 },
          borderRadius: RADIUS,
          border: `1px solid ${LINE}`,
          bgcolor: CARD_BG,
          display: "grid",
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, letterSpacing: "-0.02em", mb: 0.75 }}>
            {tiers.weekOneOnly.label}
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.95rem", lineHeight: 1.5, maxWidth: 760, mb: 1.5 }}>
            Everything listed in the Supporter and Sponsor tiers above still applies. It is just scoped to the first five
            Neighborhood Classics shows (1/11–1/15). Week 2 Times Square nights are not included.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {tiers.weekOneDates.map((d) => (
              <DateChip key={`w1-${d}`} label={d} accent={accent} />
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              border: `1px solid ${LINE}`,
              bgcolor: "rgba(255,255,255,0.03)",
            }}
          >
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", mb: 0.5 }}>
              Supporter · 1st week
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, fontSize: "1.55rem", letterSpacing: "-0.03em" }}>
              {tiers.weekOneOnly.supporterPrice}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.85rem", mt: 0.75, lineHeight: 1.45 }}>
              Same as full-tour Supporter, cut to the first five shows.
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              border: `1px solid color-mix(in srgb, ${accent} 45%, ${LINE})`,
              bgcolor: "rgba(255,255,255,0.03)",
            }}
          >
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.72rem", letterSpacing: "0.1em", textTransform: "uppercase", mb: 0.5 }}>
              Sponsor · 1st week
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 700, fontSize: "1.55rem", letterSpacing: "-0.03em" }}>
              {tiers.weekOneOnly.sponsorPrice}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.85rem", mt: 0.75, lineHeight: 1.45 }}>
              Same as full-tour Sponsor, cut to the first five shows.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Section>
  );
}

function DateChip({ label, accent }: { label: string; accent: string }) {
  return (
    <Box
      sx={{
        px: 1.1,
        py: 0.65,
        borderRadius: "10px",
        border: `1px solid ${LINE}`,
        bgcolor: "rgba(255,255,255,0.03)",
        fontFamily: portalFont,
        fontSize: "0.85rem",
        fontWeight: 600,
        color: "#fff",
        letterSpacing: "-0.02em",
        boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${accent} 18%, transparent)`,
      }}
    >
      {label}
    </Box>
  );
}

function TierCard({
  name,
  price,
  accent,
  youGet,
  youProvide,
  highlight = false,
}: {
  name: string;
  price: string;
  accent: string;
  youGet: string[];
  youProvide: string[];
  highlight?: boolean;
}) {
  return (
    <Box
      sx={{
        p: { xs: 2.25, md: 2.75 },
        borderRadius: RADIUS,
        border: highlight ? `1px solid color-mix(in srgb, ${accent} 55%, ${LINE})` : `1px solid ${LINE}`,
        bgcolor: CARD_BG,
        boxShadow: highlight ? `0 0 0 1px color-mix(in srgb, ${accent} 20%, transparent)` : "none",
        minHeight: { md: 420 },
      }}
    >
      <Typography sx={{ ...eyebrowSx, color: accent, mb: 0.5 }}>{name}</Typography>
      <Typography
        sx={{
          fontFamily: portalFont,
          fontWeight: 700,
          color: "#fff",
          fontSize: { xs: "2rem", md: "2.25rem" },
          letterSpacing: "-0.04em",
          lineHeight: 1,
          mb: 2,
        }}
      >
        {price}
      </Typography>
      <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
        You get
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.1, display: "grid", gap: 0.85, mb: 2.25 }}>
        {youGet.map((item) => (
          <Typography
            key={item}
            component="li"
            sx={{ fontFamily: portalFont, color: "rgba(244,245,247,0.8)", fontSize: "0.88rem", lineHeight: 1.45 }}
          >
            {item}
          </Typography>
        ))}
      </Box>
      <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1 }}>
        You provide
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.1, display: "grid", gap: 0.85 }}>
        {youProvide.map((item) => (
          <Typography
            key={item}
            component="li"
            sx={{ fontFamily: portalFont, color: "rgba(244,245,247,0.8)", fontSize: "0.88rem", lineHeight: 1.45 }}
          >
            {item}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function ContentEngineSection() {
  const cfg = useFoodTour();
  const nightCount = cfg.venues.length;
  const beats = [
    {
      title: "Owner stories",
      body: "Fans and business owners connecting on camera, including nights where owners themselves step into the spotlight.",
      image: cfg.images.content.ownerInterview,
      objectPosition: "center 42%",
    },
    {
      title: "Comedian on site",
      body: "Talent walking the room, tasting the food, and giving each venue a voice that travels past the night.",
      image: cfg.images.content.comedianOnSite,
      objectPosition: "center 22%",
    },
    {
      title: "Kitchen and BTS",
      body: "Guests experiencing the restaurants, green-room moments, and the after-hours transformation of the space.",
      image: cfg.images.content.kitchenMoment,
      objectPosition: "center center",
    },
    {
      title: "Brand-integrated social",
      body: `Story posts, grid posts, collaborative venue content, and a tour recap that stitches ${nightCount} nights together.`,
      image: cfg.images.content.socialAsset,
      objectPosition: "center center",
    },
  ];
  return (
    <Section id="content" eyebrow="Content engine" title="Every restaurant produces a story.">
      <Typography sx={{ ...bodySx, mb: { xs: 2, md: 2.5 }, maxWidth: 620 }}>
        ComedyUO promotes through owned stories and short-form, collaborates with venues that hold a combined 1.375M followers, and dedicates 10% of sponsorship revenue to tour ad spend plus influencer partners who have publicized ComedyUO nights before.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {beats.map((beat) => (
          <Box
            key={beat.title}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "0.9fr 1.1fr" },
              gap: 0,
              borderRadius: RADIUS,
              overflow: "hidden",
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              minHeight: { sm: 210 },
            }}
          >
            <Box sx={{ position: "relative", minHeight: { xs: 180, sm: "100%" }, bgcolor: "#151515" }}>
              <MediaFill src={beat.image} alt={beat.title} objectPosition={beat.objectPosition} />
            </Box>
            <Box sx={{ p: { xs: 2.25, md: 2.75 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: portalFont, fontWeight: 700, color: "#fff", mb: 1, letterSpacing: "-0.02em" }}>
                {beat.title}
              </Typography>
              <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.95rem", lineHeight: 1.55 }}>
                {beat.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function ReachSection() {
  const cfg = useFoodTour();
  return (
    <Section id="reach" eyebrow="Audience and reach" title="A sold-out room with a loud afterlife.">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
          gap: 1.5,
        }}
      >
        {cfg.stats.map((stat) => (
          <Box
            key={stat.label}
            sx={{
              p: { xs: 2.25, md: 2.75 },
              borderRadius: RADIUS,
              border: `1px solid ${LINE}`,
              bgcolor: CARD_BG,
              minHeight: 160,
            }}
          >
            <Typography sx={{ ...eyebrowSx, mb: 1.25 }}>{stat.label}</Typography>
            <Typography
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                color: "#fff",
                fontSize: { xs: "2rem", md: "2.35rem" },
                letterSpacing: "-0.04em",
                lineHeight: 1,
                mb: 1,
              }}
            >
              {stat.value}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.9rem", lineHeight: 1.5 }}>
              {stat.detail}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function VisionSection() {
  const cfg = useFoodTour();
  const accent = cfg.accentColor;
  const brand = cfg.brandName.trim();
  const hasBody = Boolean(cfg.cta.body.trim());
  const hasContactPerson = Boolean(cfg.cta.name.trim() || cfg.cta.role.trim());
  return (
    <Section id="vision" eyebrow="Partnership vision" title={cfg.cta.headline}>
      <Box
        sx={{
          p: { xs: 3, md: 4.5 },
          borderRadius: RADIUS,
          border: `1px solid ${LINE}`,
          background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 18%, transparent), rgba(255,255,255,0.03))`,
          display: "grid",
          gap: 2.5,
          maxWidth: 820,
        }}
      >
        {hasBody ? (
          <Typography sx={{ ...bodySx, color: "rgba(244,245,247,0.86)", fontSize: { xs: "1.05rem", md: "1.15rem" } }}>
            {cfg.cta.body}
          </Typography>
        ) : null}
        <Box>
          {hasContactPerson ? (
            <>
              {cfg.cta.name.trim() ? (
                <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 600 }}>{cfg.cta.name}</Typography>
              ) : null}
              {cfg.cta.role.trim() ? (
                <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.9rem", mb: 2 }}>{cfg.cta.role}</Typography>
              ) : null}
            </>
          ) : null}
          <Button
            href={`mailto:${cfg.cta.email}?subject=${encodeURIComponent(
              brand ? `${brand} × ComedyUO Food Tour` : "ComedyUO Food Tour partnership"
            )}`}
            sx={{
              fontFamily: portalFont,
              fontWeight: 600,
              textTransform: "none",
              color: "#0a0a0a",
              bgcolor: "#fff",
              borderRadius: "999px",
              px: 2.5,
              py: 1.15,
              "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
            }}
          >
            Email {cfg.cta.email}
          </Button>
        </Box>
      </Box>
    </Section>
  );
}

function Footer({ contactEmail }: { contactEmail: string }) {
  const brand = useFoodTour().brandName.trim();
  return (
    <Box component="footer" sx={{ borderTop: `1px solid ${LINE}`, py: 4, px: { xs: 2.5, md: 4 } }}>
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.8rem" }}>
          {brand ? `ComedyUO × ${brand} · Food Tour partnership concept` : "ComedyUO Food Tour · Partnership concept"}
        </Typography>
        <Link href={`mailto:${contactEmail}`} underline="hover" sx={{ fontFamily: portalFont, color: "#fff", fontSize: "0.85rem" }}>
          {contactEmail}
        </Link>
      </Box>
    </Box>
  );
}

function Section({
  id,
  eyebrow,
  title,
  children,
}: {
  id: string;
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  const accent = useFoodTour().accentColor;
  return (
    <Box
      id={id}
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        py: { xs: 5.5, md: 8 },
        scrollMarginTop: 88,
      }}
    >
      <FadeUp>
        <Typography sx={{ ...eyebrowSx, color: accent }}>{eyebrow}</Typography>
        <Typography
          component="h2"
          sx={{
            fontFamily: portalFont,
            fontWeight: 700,
            color: "#fff",
            fontSize: "clamp(1.65rem, 2.2vw + 1rem, 2.55rem)",
            lineHeight: 1.12,
            letterSpacing: "-0.03em",
            mb: { xs: 2, md: 2.5 },
            maxWidth: 780,
          }}
        >
          {title}
        </Typography>
        {children}
      </FadeUp>
    </Box>
  );
}

function MediaFill({
  src,
  alt,
  objectPosition = "center center",
}: {
  src: string;
  alt: string;
  objectPosition?: string;
}) {
  if (!src) {
    return (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "#171717",
          backgroundImage:
            "linear-gradient(135deg, rgba(255,255,255,0.04), transparent 40%), radial-gradient(circle at 30% 20%, rgba(212,165,116,0.18), transparent 55%)",
        }}
        aria-label={alt}
      />
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition,
        display: "block",
      }}
    />
  );
}

function MediaFrame({
  src,
  alt,
  ratio,
  objectPosition = "center center",
}: {
  src: string;
  alt: string;
  ratio: string;
  objectPosition?: string;
}) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: ratio,
        borderRadius: RADIUS,
        overflow: "hidden",
        border: `1px solid ${LINE}`,
        bgcolor: "#151515",
      }}
    >
      <MediaFill src={src} alt={alt} objectPosition={objectPosition} />
    </Box>
  );
}

function FadeUp({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(18px)",
        transition: "opacity 700ms ease, transform 700ms ease",
      }}
    >
      {children}
    </Box>
  );
}

const eyebrowSx = {
  fontFamily: portalFont,
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.16em",
  textTransform: "uppercase" as const,
  color: MUTED,
  mb: 1.25,
};

const bodySx = {
  fontFamily: portalFont,
  color: MUTED,
  fontSize: { xs: "0.95rem", md: "1.02rem" },
  lineHeight: 1.45,
};
